/**
 * Custom Menu
 *
 * Sidebar navigation menu with collapsible behavior.
 * Features styled menu items with hover and active states.
 */

import React from 'react';
import { useTranslate, useSidebarState } from 'react-admin';
import { Box, useMediaQuery, MenuItem as MuiMenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DashboardRounded as DashboardIcon,
  ContactsRounded as LeadsIcon,
  ApartmentRounded as HotelsIcon,
  AccountTreeRounded as ChainsIcon,
  CategoryRounded as CatalogsIcon,
  BackupRounded as BackupsIcon,
} from '@mui/icons-material';
import { USER_ROLES } from '@/config/constants';
import { hasRole } from '@/utils/auth';

interface StyledMenuItemProps {
  open: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
  shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuItemProps>(({ theme, open }) => ({
  margin: theme.custom.menu.item.margin,
  padding: theme.custom.menu.item.padding,
  borderRadius: theme.custom.menu.item.borderRadius,
  color: theme.custom.menu.item.color(theme),
  justifyContent: open ? 'flex-start' : 'center',

  '& .MuiListItemText-primary': {
    fontWeight: 600,
  },

  display: 'flex',
  gap: theme.custom.menu.item.gap,
  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',

  '& .MuiListItemIcon-root': {
    minWidth: 'auto',
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  '&:hover:not(.active)': {
    color: theme.custom.menu.item.hover.color(theme),
    backgroundColor: open ? theme.custom.menu.item.hover.backgroundColor(theme) : 'transparent',
  },

  '&.active': {
    color: open ? theme.custom.menu.item.active.color(theme) : theme.palette.primary.main,
    backgroundColor: open ? theme.custom.menu.item.active.backgroundColor(theme) : 'transparent',
    fontWeight: theme.custom.menu.item.active.fontWeight,
    boxShadow: open ? theme.custom.menu.item.active.boxShadow(theme) : 'none',

    '& .MuiListItemIcon-root': {
      color: open ? 'inherit' : theme.custom.menu.item.closedActiveIcon.color(theme),
      backgroundColor: open ? 'transparent' : theme.custom.menu.item.closedActiveIcon.backgroundColor(theme),
      padding: open ? 0 : '6px',
      borderRadius: open ? 0 : (theme.custom.menu.item.closedActiveIcon.borderRadius || '6px'),
    },

    '&:hover': {
      backgroundColor: open ? theme.custom.menu.item.active.hover.backgroundColor(theme) : 'transparent',
    },
  },
}));

/**
 * Menu items configuration
 *
 * CUSTOMIZE: Add your menu items here
 */
interface MenuItemDef {
  name: string;
  to: string;
  icon: React.ReactNode;
  textKey: string;
  superuserOnly?: boolean;
}

const menuItems: MenuItemDef[] = [
  {
    name: 'dashboard',
    to: '/',
    icon: <DashboardIcon />,
    textKey: 'ra.page.dashboard',
  },
  {
    name: 'leads',
    to: '/leads',
    icon: <LeadsIcon />,
    textKey: 'resources.leads.name',
  },
  {
    name: 'hotels',
    to: '/hotels',
    icon: <HotelsIcon />,
    textKey: 'resources.hotels.name',
  },
  {
    name: 'hotel-chains',
    to: '/hotel-chains',
    icon: <ChainsIcon />,
    textKey: 'resources.hotel-chains.name',
  },
  {
    name: 'catalogs',
    to: '/catalogs',
    icon: <CatalogsIcon />,
    textKey: 'catalogs.menu',
  },
  {
    name: 'backups',
    to: '/backups',
    icon: <BackupsIcon />,
    textKey: 'backups.menu',
    superuserOnly: true,
  },
];

export const CustomMenu: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useSidebarState();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const isActiveRoute = (to: string): boolean => location.pathname === to;

  const handleMenuClick = (to: string): void => {
    navigate(to);
    if (isSmallScreen) {
      setSidebarOpen(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isSmallScreen ? '100vh' : 'calc(100vh - 44px - 16px)',
        boxSizing: 'border-box',
        backgroundColor: (theme.palette.background as any).menu || theme.palette.background.paper,
        padding: '12px',
        borderRadius: theme.custom.layout.card.borderRadius,
        width: isSmallScreen
          ? '94%'
          : isSidebarOpen
          ? theme.custom.menu.openWidth
          : theme.custom.menu.closedWidth,
        minWidth: isSmallScreen
          ? 'auto'
          : isSidebarOpen
          ? theme.custom.menu.openWidth
          : theme.custom.menu.closedWidth,
        maxWidth: isSmallScreen
          ? '100%'
          : isSidebarOpen
          ? theme.custom.menu.openWidth
          : theme.custom.menu.closedWidth,
        transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {menuItems
          .filter((item) => !item.superuserOnly || hasRole(USER_ROLES.SUPERUSER))
          .map((item) => (
          <StyledMenuItem
            key={item.name}
            onClick={() => handleMenuClick(item.to)}
            open={isSidebarOpen}
            className={isActiveRoute(item.to) ? 'active' : ''}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={translate(item.textKey, { smart_count: 1 })}
              sx={{
                transition: 'opacity 0.2s ease-in-out',
                opacity: isSidebarOpen || isSmallScreen ? 1 : 0,
              }}
            />
          </StyledMenuItem>
        ))}
      </Box>
    </Box>
  );
};

export default CustomMenu;
